import { useEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import {
  leadsEndpoints,
  leadsListCountEndpoint,
  leadsListStatsEndpoint,
} from "@/config/endpoints";
import { useAppSelector } from "@/stores/hook";
import { store } from "@/stores/store";
import { queryClient as defaultQueryClient } from "@/lib/queryClient";
import { serializeFilterParams } from "./useLeadFilterParams";
import { LEADS_PAGE_SIZE } from "./useLeads";

const LEADS_PREFETCH_STALE_MS = 5 * 60 * 1000;

/**
 * Plain (non-hook) primitive that warms the lead-list trio in the React
 * Query cache. Safe to call from anywhere — auth hooks, route guards,
 * imperative event handlers — without forcing a component re-render.
 *
 * Reads filters from the Redux store directly so it can run before any
 * React tree has mounted (the leads hooks would otherwise need to be
 * inside a component). React Query dedupes by query key, so calling
 * this multiple times in a session is a no-op after the first hit.
 */
let _prefetchFiredOnce = false;
export function triggerLeadsPrefetch(qc: QueryClient = defaultQueryClient) {
  if (_prefetchFiredOnce) return;
  _prefetchFiredOnce = true;

  // Snapshot the current filter state directly from Redux. The leads
  // page consumes the same `useLeadFilterParams` selector → same filter
  // shape → same query key → cache hit on mount.
  const state = store.getState();
  const slice = (state as any).leadFilterSlice ?? {};
  const filters = serializeFilterParams(
    slice.appliedFilters ?? {},
    slice.search,
  );

  void qc.prefetchInfiniteQuery({
    queryKey: ["leads", "infinite", filters],
    initialPageParam: 1,
    staleTime: LEADS_PREFETCH_STALE_MS,
    queryFn: async () => {
      const res = await axiosInstance.get(leadsEndpoints, {
        params: { ...filters, page: 1, limit: LEADS_PAGE_SIZE },
      });
      return res.data;
    },
  });
  void qc.prefetchQuery({
    queryKey: ["leads-list-count", filters],
    staleTime: LEADS_PREFETCH_STALE_MS,
    queryFn: async () => {
      const res = await axiosInstance.get(leadsListCountEndpoint, {
        params: filters,
      });
      return Number(res.data?.total) || 0;
    },
  });
  void qc.prefetchQuery({
    queryKey: ["leads-list-stats", filters],
    staleTime: LEADS_PREFETCH_STALE_MS,
    queryFn: async () => {
      const res = await axiosInstance.get(leadsListStatsEndpoint, {
        params: filters,
      });
      return (res.data?.stats ?? {}) as Record<string, any>;
    },
  });
}

/** Reset on logout so the next session re-warms the cache. */
export function resetLeadsPrefetchGuard() {
  _prefetchFiredOnce = false;
}

/**
 * Aggressive background prefetch of the lead-list trio (`/leads`,
 * `/leads/list-count`, `/leads/list-stats`) the moment the authenticated
 * app boots. By the time the user clicks "Leads" in the sidebar the data
 * is already in the React Query cache, so the page renders synchronously
 * off the cache hit instead of waiting for a fresh round-trip.
 *
 * Design choices:
 * - Fires SYNCHRONOUSLY on the first useEffect (no setTimeout). A defer
 *   would race with a fast click on "Leads" — by the time the timer
 *   fires, the route's own hook has already started fetching.
 * - Does NOT skip when the user lands directly on /leads. React Query
 *   dedupes by query key, so the route's own hook joins the in-flight
 *   prefetch instead of starting a parallel one — no wasted network.
 * - `staleTime: 5 minutes` on the prefetched entries so a quick
 *   navigation to /leads doesn't trigger an immediate background
 *   refetch on top of the cached data.
 * - Same query keys + filter shape as `useLeads` / `useLeadsListCount`
 *   / `useLeadsListStats`, so the leads page lands on a hot cache hit.
 * - Fires ONCE per session (`useRef` guard) — subsequent navigations
 *   between authenticated routes don't re-trigger.
 */
export default function LeadsPrefetch() {
  const qc = useQueryClient();
  // Cheap subscription — only re-renders when userId actually changes,
  // not on every Redux dispatch (it's a primitive value, no reference
  // churn). The actual prefetch reads filters from the store snapshot
  // inside `triggerLeadsPrefetch`, no selector overhead per render.
  const userId = useAppSelector((state) => state.auth.currentUser?.user?.id);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!userId) return;
    firedRef.current = true;
    triggerLeadsPrefetch(qc);
  }, [userId, qc]);

  return null;
}
